import type { UserProfile, UserProfileRead } from "epsm-api-types/models";
import type { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { getDirtyValues } from "@/components/user/lib/helpers/getDirtyValues";
import {
	investingCharacteristicLabels,
	investingStyleLabels,
	onlineAddressLabels,
} from "@/components/user/profile/config";
import type { FormSchema } from "@/components/user/profile/types";
import { cn } from "@/lib/utils";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { ProfileImageManager } from "../ProfileImageManager";
import { Rating } from "../Rating";

interface UpdateProfileFormProps {
	form: UseFormReturn<FormSchema>;
	userData: Partial<UserProfile & UserProfileRead>;
	updateUserData: (data: Partial<UserProfile>) => Promise<void>;
	isLoadingUpdate: boolean;
}

export const UpdateProfileForm = ({
	form,
	userData,
	updateUserData,
	isLoadingUpdate,
}: UpdateProfileFormProps) => {
	const {
		control,
		handleSubmit,
		setValue,
		getValues,
		watch,
		formState: { isValid, dirtyFields, isDirty },
	} = form;

	const investingCharacteristics: FormSchema["investingCharacteristics"] =
		watch("investingCharacteristics") ?? {};

	const investingStyles: FormSchema["investingCharacteristics"]["investingStyle"] =
		investingCharacteristics?.investingStyle ?? {};

	const onlineAddresses: FormSchema["onlineAddresses"] =
		watch("onlineAddresses") ?? {};

	const publicProfile = watch("publicProfile");
	const showOnlineAddressesOnPublicProfile = watch("showOnlineAddresses");

	const renderPublicInvestingCharacteristics = () => {
		return Object.entries(investingCharacteristics)
			.filter(([key]) => key !== "investingStyle")
			.map(([key, value]) => (
				<div
					key={key}
					className="flex items-center justify-between gap-2 text-lg"
				>
					<p>
						{
							investingCharacteristicLabels[
								key as keyof typeof investingCharacteristicLabels
							]
						}
					</p>
					<Rating value={Number(value)} readOnly />
				</div>
			));
	};

	const renderPublicOnlineAddresses = () => {
		return Object.entries(onlineAddresses)
			.filter(([_, value]) => value?.trim())
			.map(([key, value]) => (
				<div
					key={String(key)}
					className="flex gap-2 items-center justify-between"
				>
					<p className="text-lg">
						{onlineAddressLabels[key as keyof typeof onlineAddressLabels]}
					</p>
					{key === "website" ? (
						<a
							href={value}
							target="_blank"
							rel="noreferrer"
							className="text-link hover:underline text-lg"
						>
							{value}
						</a>
					) : (
						<p className="text-lg">{value}</p>
					)}
				</div>
			));
	};

	const renderPublicInvestingStyle = () => {
		return Object.entries(investingStyles)
			.filter(([key, value]) => key !== "all" && value)
			.map(([key]) => (
				<span
					key={key}
					className="flex px-4 py-1 border border-gold rounded-full text-sm bg-gold text-black"
				>
					{investingStyleLabels[key as keyof typeof investingStyleLabels]}
				</span>
			));
	};

	const onSubmit = async (formValues: Partial<UserProfile>) => {
		try {
			const dirtyValues = getDirtyValues(dirtyFields, formValues);

			if (!Object.keys(dirtyValues).length) return;

			if ("investingCharacteristics" in dirtyValues) {
				dirtyValues.investingCharacteristics = getValues(
					"investingCharacteristics",
				);
			}

			if ("onlineAddresses" in dirtyValues) {
				dirtyValues.onlineAddresses = getValues("onlineAddresses");
			}

			await updateUserData(dirtyValues);
		} catch (error) {
			ErrorHandler.logError(error);
		}
	};

	return (
		<Form {...form}>
			<form
				onSubmit={handleSubmit(onSubmit)}
				data-testid="update-profile-form"
				className="grid grid-cols-[minmax(0,32rem)_minmax(0,32rem)] grid-rows-[auto_auto_auto_auto_auto_auto_auto] gap-10 max-w-[--breakpoint-xl] w-full justify-evenly"
			>
				<div>
					<h5 className="col-span-1 row-start-1 order-1 text-xl font-medium">
						Public Profile
					</h5>
					<Separator className="mt-3" />
				</div>
				<div>
					<h5 className="col-span-1 row-start-1 order-1 text-xl font-medium">
						Private Details
					</h5>
					<Separator className="mt-3" />
				</div>

				<section
					data-testid="public-user-section"
					className="col-start-1 row-start-2 flex flex-col gap-4 w-full"
				>
					<ProfileImageManager control={control} setValue={setValue} />

					<FormField
						control={control}
						name="userName"
						render={({ field }) => (
							<FormItem className="flex flex-col gap-1">
								<FormLabel>Username</FormLabel>
								<FormControl>
									<Input
										placeholder="Name/Alias"
										className="text-lg"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={control}
						name="aboutMe"
						render={({ field }) => (
							<FormItem className="flex flex-col gap-1">
								<FormLabel>About me</FormLabel>
								<FormControl>
									<Textarea
										placeholder="Tell others about yourself"
										className="text-lg"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={control}
						name="goal"
						render={({ field }) => (
							<FormItem className="flex flex-col gap-1">
								<FormLabel>Goal</FormLabel>
								<FormControl>
									<Textarea
										placeholder="Tell others about the type of stocks you're looking for"
										className="text-lg"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</section>

				<section
					data-testid="private-user-section"
					className="col-start-2 row-start-2 flex flex-col gap-4 w-full"
				>
					<div className="flex gap-2 items-center justify-between">
						<div className="flex gap-2 items-center">
							<p className="min-w-[100px] text-[var(--light-gray)] text-lg">
								First Name
							</p>
							<p className="text-lg">{userData?.firstName}</p>
						</div>
						<Button
							disabled={!isValid || !isDirty || isLoadingUpdate}
							type="submit"
							variant="dropdown"
							className="text-[16px] w-fit"
						>
							Update
						</Button>
					</div>
					<div className="flex gap-2 items-center">
						<p className="min-w-[100px] text-[var(--light-gray)] text-lg">
							Last Name
						</p>
						<p className="text-lg">{userData?.lastName}</p>
					</div>
					<div className="flex gap-2 items-center">
						<p className="min-w-[100px] text-[var(--light-gray)] text-lg">
							Email
						</p>
						<p className="text-lg">{userData?.email}</p>
					</div>
					<div className="flex gap-2 items-center">
						<p className="min-w-[100px] text-[var(--light-gray)] text-lg">
							Phone
						</p>
						<p className="text-lg">{userData?.phone}</p>
					</div>
				</section>

				{Object.values(investingStyles).some(Boolean) && (
					<section
						data-testid="public-investing-styles-section"
						className="col-start-1 row-start-3 flex flex-col gap-4 w-full"
					>
						<p className="text-lg text-[var(--light-gray)]">Investing Style</p>
						<div className="flex flex-wrap gap-2">
							{renderPublicInvestingStyle()}
						</div>
					</section>
				)}

				<section
					data-testid="private-investing-styles-section"
					className="col-start-2 row-start-3 flex flex-col gap-4 w-full"
				>
					<p className="text-lg text-[var(--light-gray)]">Investing Style</p>
					<div className="flex flex-wrap gap-2">
						{(
							Object.keys(investingStyleLabels) as Array<
								keyof FormSchema["investingCharacteristics"]["investingStyle"]
							>
						).map((key) => (
							<FormField
								key={key}
								control={control}
								name={`investingCharacteristics.investingStyle.${key}`}
								render={({ field }) => {
									const handleChange = (checked: boolean) => {
										const currentStyles = getValues(
											"investingCharacteristics.investingStyle",
										) as Record<
											keyof FormSchema["investingCharacteristics"]["investingStyle"],
											boolean
										>;

										if (key === "all") {
											for (const style of Object.keys(currentStyles) as Array<
												keyof typeof currentStyles
											>) {
												setValue(
													`investingCharacteristics.investingStyle.${style}`,
													checked,
													{ shouldDirty: true, shouldValidate: true },
												);
											}
										} else {
											setValue(
												`investingCharacteristics.investingStyle.${key}`,
												checked,
												{ shouldDirty: true, shouldValidate: true },
											);

											const updatedAllSelected = (
												Object.keys(currentStyles) as Array<
													keyof typeof currentStyles
												>
											)
												.filter((style) => style !== "all")
												.every((style) =>
													getValues(
														`investingCharacteristics.investingStyle.${style}`,
													),
												);

											setValue(
												"investingCharacteristics.investingStyle.all",
												updatedAllSelected,
												{ shouldDirty: true, shouldValidate: true },
											);
										}
									};

									return (
										<FormItem>
											<FormLabel
												htmlFor={key}
												className={cn(
													"flex px-4 py-1 border border-gold rounded-full text-sm transition-colors duration-200 cursor-pointer mb-0",
													field.value ? "bg-gold text-black" : "text-gold",
												)}
											>
												{investingStyleLabels[key]}
											</FormLabel>
											<FormControl>
												<Checkbox
													checked={Boolean(field.value)}
													onCheckedChange={handleChange}
													className="hidden"
													id={key}
												/>
											</FormControl>
										</FormItem>
									);
								}}
							/>
						))}
					</div>
				</section>

				<section
					data-testid="public-investing-characteristics-section"
					className="col-start-1 row-start-4 flex flex-col gap-5 w-full"
				>
					<p className="text-lg text-[var(--light-gray)]">
						Investing Characteristics
					</p>
					<div
						data-testid="public-characteristics"
						className={cn(
							"flex flex-col gap-3",
							!publicProfile && "opacity-50",
						)}
					>
						{renderPublicInvestingCharacteristics()}
					</div>
				</section>

				<section
					data-testid="private-investing-characteristics-section"
					className="col-start-2 row-start-4 flex flex-col gap-5 w-full"
				>
					<div className="flex items-center gap-2 justify-between">
						<p className="text-lg text-[var(--light-gray)]">
							Investing Characteristics
						</p>
						<FormField
							control={control}
							name="publicProfile"
							render={({ field }) => (
								<FormItem className="space-y-0 flex items-center justify-between">
									<FormLabel className="text-md pr-2 text-[var(--light-gray)]">
										Show on Public Profile
									</FormLabel>
									<FormControl>
										<Checkbox
											checked={field.value}
											onCheckedChange={field.onChange}
											className="mt-0!"
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					</div>

					<div className="flex flex-col gap-3">
						{Object.entries(investingCharacteristicLabels).map(
							([key, label]) => (
								<FormField
									key={key}
									control={control}
									name={`investingCharacteristics.${key}` as keyof FormSchema}
									render={({ field }) => (
										<FormItem className="flex items-center justify-between space-y-0">
											<p className="text-lg">{label}</p>
											<FormControl>
												<Rating
													value={Number(field.value)}
													onChange={field.onChange}
													type={
														key === "riskTolerance"
															? "riskTolerance"
															: key === "investmentHorizon"
																? "investmentHorizon"
																: "default"
													}
												/>
											</FormControl>
										</FormItem>
									)}
								/>
							),
						)}
					</div>
				</section>

				{Object.values(onlineAddresses).some(Boolean) && (
					<section
						data-testid="public-online-addresses-section"
						className="col-start-1 row-start-5 flex flex-col gap-5 w-full"
					>
						<p className="text-lg text-[var(--light-gray)]">Online Addresses</p>
						<div
							data-testid="public-addresses"
							className={cn(
								"flex flex-col gap-4",
								!showOnlineAddressesOnPublicProfile && "opacity-50",
							)}
						>
							{renderPublicOnlineAddresses()}
						</div>
					</section>
				)}

				{false && (
					<>
						<section
							data-testid="private-online-addresses-section"
							className="col-start-2 row-start-5 flex flex-col gap-5 w-full"
						>
							<div className="flex items-center gap-3 justify-between">
								<p className="text-lg text-[var(--light-gray)]">
									Online Addresses
								</p>
								<FormField
									control={control}
									name="showOnlineAddresses"
									render={({ field }) => (
										<FormItem className="space-y-0 flex items-center justify-between">
											<FormLabel className="text-md pr-2 text-[var(--light-gray)]">
												Show on Public Profile
											</FormLabel>
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
													className="mt-0!"
												/>
											</FormControl>
										</FormItem>
									)}
								/>
							</div>

							<div className="flex flex-col gap-7">
								{Object.entries(onlineAddressLabels).map(([key, label]) => (
									<FormField
										key={key}
										control={control}
										name={`onlineAddresses.${key}` as keyof FormSchema}
										render={({ field }) => (
											<FormItem className="flex flex-col gap-1">
												<FormLabel>{label}</FormLabel>
												<FormControl>
													<Input
														placeholder="Name"
														className="text-lg"
														{...field}
														value={field.value as string}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								))}
							</div>
						</section>

						<section className="col-start-2 row-start-6 w-full flex justify-end">
							<Button
								disabled={!isValid || !isDirty || isLoadingUpdate}
								type="submit"
								variant="dropdown"
								className="text-[16px] w-fit"
							>
								Update
							</Button>
						</section>
					</>
				)}
			</form>
		</Form>
	);
};
